import type { MillsheetSortConfig } from '@mbfab/shared'
import { chunk } from 'es-toolkit'
import { FileSpreadsheet, FileText, Printer } from 'lucide-react'
import type { Dispatch, SetStateAction } from 'react'
import { Fragment, useMemo, useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'
import { z } from 'zod'

import { buildPrintRows, buildSummaryItems } from './print/build-print-items'
import { downloadMillsheetCsv } from './print/csv-download'
import {
  downloadMillsheetListExcel,
  groupMillsheetsByConstruction,
  millsheetsToExcelItems,
} from './print/excel-download'
import {
  fixColumnOrderFor,
  HOME_EXCEL_COLUMN_LABEL_OVERRIDES,
  initialHomeExcelColumnConfigs,
  initialSummaryColumnConfigs,
  printColumnTypeLabel,
  REORDERABLE_COLUMN_TYPES,
  resolvePrintSortDirection,
  restoredMillsheetColumnConfigsSchema,
  SLICE_NUMBER,
  SUMMARY_COLUMN_COUNT_WARNING_THRESHOLD,
} from './print/home-print-columns'
import type { PrintDialogState, PrintTargetFlags, S3ObjectForPrint } from './print/print-types'
import { printTargetFlagsSchema } from './print/print-types'
import { sortMillsheetsForPrint } from './print/sort-millsheets-for-print'
import type { MillsheetRow } from './types'

import { PrintColumnSettingsSection, reorderColumnConfigs } from '@/components/app/print-column-settings'
import type { PrintDialogTab } from '@/components/app/print-dialog-shell'
import {
  PrintColumnCheckboxRow,
  PrintDetailsSection,
  PrintDialogField,
  PrintDialogFooter,
  PrintDialogPreviewPane,
  PrintDialogPreviewToolbar,
  PrintDialogSettingsBody,
  PrintDialogSettingsPane,
  PrintDialogShell,
  PrintDialogStatusPanes,
  PrintDialogTabNav,
  PrintExportBody,
  PrintExportCard,
  PrintExportPreviewPane,
  PrintTargetRow,
  usePrintPreviewZoom,
} from '@/components/app/print-dialog-shell'
import {
  buildPrintPagePlan,
  buildPrintPagePlanSources,
  buildSummaryPageLabels,
  findMillsheetStartPageNo,
  hasPrintableMillsheetPage,
} from '@/components/app/print-page-plan'
import { PrintPlannedPage } from '@/components/screens/order-matching/print/print-s3-object'
import { pageSize, pageStyle } from '@/components/screens/order-matching/print/print-style'
import type { HeaderLabelTextConfig } from '@/components/screens/order-matching/print/svg/header'
import {
  DEFAULT_CONSTRUCTION_EXECUTOR,
  DEFAULT_CONSTRUCTION_MANAGER,
  DEFAULT_SUMMARY_TITLE,
  headerLabelTextConfigSchema,
} from '@/components/screens/order-matching/print/svg/header'
import type { ColumnConfig } from '@/components/screens/order-matching/print/svg/main-row'
import { MillsheetListSvgV2 } from '@/components/screens/order-matching/print/svg/millsheet-list-svg'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { APP_LOCAL_STORAGE_KEYS } from '@/lib/local-storage-keys'
import { useLocalStorageState } from '@/lib/use-local-storage-state'

//
// ホーム（ミルシート一覧）「印刷・データ出力」ダイアログ。
// v1 apps/frontend/client/src/components/MillsheetPrintForm/MillsheetPrintForm.tsx の移植。
// 見た目・構造は order-matching/print-dialog.tsx に揃えている（「印刷設定」/「データ出力」の2タブ）。
// v1 と同じ localStorage キーで印刷設定を保存する点だけが発注照合と異なる。
//
// 表示項目設定は集計表（印刷）と使用鋼材一覧表（Excel）で1つの設定を共有している（v1 由来）。
// 発注照合は集計表用とExcel用で別々の設定を持つが、ここを分けると利用者の保存済み設定の
// 意味が変わってしまうため統合したままにし、代わりに同じセクションを両タブに出す
// （タブは同時に1つしか表示されないので画面上で重複はしない）。
//

function SummaryColumnWarning({ count }: { count: number }) {
  return (
    <Callout testId="millsheet-summary-column-warning" className="text-xs">
      項目数が多いとレイアウトが崩れることがあります（現在{count}項目）。項目を増やすほど1列の幅が狭くなります。
    </Callout>
  )
}

export function MillsheetPrintDialog({
  open,
  state,
  selectedCount,
  millsheetSortConfig,
  onClose,
}: {
  open: boolean
  state: PrintDialogState
  selectedCount: number
  millsheetSortConfig: MillsheetSortConfig
  onClose: () => void
}) {
  // OutputDateDialog / ConfirmDialog と同じく、閉じている間は丸ごとアンマウントして
  // タブ・列設定の展開状態以外の一時状態を持ち越さない
  if (!open) {
    return null
  }
  return (
    <MillsheetPrintDialogContent
      state={state}
      selectedCount={selectedCount}
      millsheetSortConfig={millsheetSortConfig}
      onClose={onClose}
    />
  )
}

function MillsheetPrintDialogContent({
  state,
  selectedCount,
  millsheetSortConfig,
  onClose,
}: {
  state: PrintDialogState
  selectedCount: number
  millsheetSortConfig: MillsheetSortConfig
  onClose: () => void
}) {
  const [tab, setTab] = useState<PrintDialogTab>('print')
  const [printTargetFlags, setPrintTargetFlags] = useLocalStorageState<PrintTargetFlags>(
    APP_LOCAL_STORAGE_KEYS.millsheetPrintTargetFlags,
    { summary: true, original: true, attachments: true },
    printTargetFlagsSchema,
  )
  // 原本の印刷順・ページ番号・Excel/CSV の行順は Excel の列設定（左右順と昇順／降順）で決まる。
  // 印刷設定タブとデータ出力タブで同じ並びを使うため、設定の状態はここで1つだけ持つ
  const [excelColumnConfigs, setExcelColumnConfigs] = useLocalStorageState<ColumnConfig[]>(
    APP_LOCAL_STORAGE_KEYS.millsheetExcelColumnConfigs,
    initialHomeExcelColumnConfigs(),
    restoredMillsheetColumnConfigsSchema(initialHomeExcelColumnConfigs()),
  )
  const sortedMillsheets = useMemo(
    () =>
      state.status === 'ready' ? sortMillsheetsForPrint(state.millsheets, millsheetSortConfig, excelColumnConfigs) : [],
    [state, millsheetSortConfig, excelColumnConfigs],
  )
  return (
    <PrintDialogShell description={<>選択した {selectedCount} 件を対象にしています</>} onClose={onClose}>
      {tab === 'print' ? (
        <PrintPanes
          tab={tab}
          onChangeTab={setTab}
          state={state}
          sortedMillsheets={sortedMillsheets}
          millsheetSortConfig={millsheetSortConfig}
          printTargetFlags={printTargetFlags}
          setPrintTargetFlags={setPrintTargetFlags}
        />
      ) : (
        <>
          <PrintDialogSettingsPane>
            <PrintDialogTabNav tab={tab} onChangeTab={setTab} />
            <ExportTab
              millsheets={sortedMillsheets}
              images={state.status === 'ready' ? state.images : []}
              printTargetFlags={printTargetFlags}
              columnConfigs={excelColumnConfigs}
              setColumnConfigs={setExcelColumnConfigs}
            />
          </PrintDialogSettingsPane>
          <PrintExportPreviewPane />
        </>
      )}
    </PrintDialogShell>
  )
}

function PrintPanes({
  tab,
  onChangeTab,
  state,
  sortedMillsheets,
  millsheetSortConfig,
  printTargetFlags,
  setPrintTargetFlags,
}: {
  tab: PrintDialogTab
  onChangeTab: (tab: PrintDialogTab) => void
  state: PrintDialogState
  sortedMillsheets: MillsheetRow[]
  millsheetSortConfig: MillsheetSortConfig
  printTargetFlags: PrintTargetFlags
  setPrintTargetFlags: Dispatch<SetStateAction<PrintTargetFlags>>
}) {
  if (state.status === 'closed') {
    return null
  }
  if (state.status === 'loading') {
    return (
      <PrintDialogStatusPanes
        status="loading"
        loadingTestId="millsheet-print-loading"
        errorTestId="millsheet-print-error"
        settingsHeader={<PrintDialogTabNav tab={tab} onChangeTab={onChangeTab} />}
      />
    )
  }
  if (state.status === 'error') {
    return (
      <PrintDialogStatusPanes
        status="error"
        loadingTestId="millsheet-print-loading"
        errorTestId="millsheet-print-error"
        settingsHeader={<PrintDialogTabNav tab={tab} onChangeTab={onChangeTab} />}
      />
    )
  }
  return (
    <PrintPanesReady
      tab={tab}
      onChangeTab={onChangeTab}
      millsheets={sortedMillsheets}
      images={state.images}
      millsheetSortConfig={millsheetSortConfig}
      printTargetFlags={printTargetFlags}
      setPrintTargetFlags={setPrintTargetFlags}
    />
  )
}

function PrintPanesReady({
  tab,
  onChangeTab,
  millsheets,
  images,
  millsheetSortConfig,
  printTargetFlags,
  setPrintTargetFlags,
}: {
  tab: PrintDialogTab
  onChangeTab: (tab: PrintDialogTab) => void
  millsheets: MillsheetRow[]
  images: S3ObjectForPrint[]
  millsheetSortConfig: MillsheetSortConfig
  printTargetFlags: PrintTargetFlags
  setPrintTargetFlags: Dispatch<SetStateAction<PrintTargetFlags>>
}) {
  const componentRef = useRef<HTMLDivElement>(null)
  const { viewportRef, zoom, ...zoomControls } = usePrintPreviewZoom()
  const [columnsOpen, setColumnsOpen] = useState(false)

  const [columnConfigs, setColumnConfigs] = useLocalStorageState<ColumnConfig[]>(
    APP_LOCAL_STORAGE_KEYS.millsheetSummaryColumnConfigs,
    initialSummaryColumnConfigs(),
    restoredMillsheetColumnConfigsSchema(initialSummaryColumnConfigs()),
  )
  const [headerLabelTextConfig, setHeaderLabelTextConfig] = useLocalStorageState<HeaderLabelTextConfig>(
    APP_LOCAL_STORAGE_KEYS.millsheetHeaderLabelTextConfig,
    {},
    headerLabelTextConfigSchema,
  )

  // 集計表（SVG）には「ページ番号」列が意味を持たない（v1 の集計表設定にも無い項目）ため、
  // fixColumnOrderFor が先頭に固定で足すページ番号を除いたものを使う
  const summaryColumnConfigs = fixColumnOrderFor(columnConfigs).filter((x) => x.type !== 'pageNumber')

  const pageSources = buildPrintPagePlanSources({ millsheets, images })
  const pagePlan = buildPrintPagePlan({
    millsheets: millsheets.map((millsheet) => ({
      id: millsheet.id,
      constructionName: millsheet.constructionName,
    })),
    sources: pageSources,
    targets: printTargetFlags,
  })
  const orderedMillsheets = pagePlan.orderedMillsheets.flatMap((plannedMillsheet) =>
    millsheets.filter((millsheet) => millsheet.id === plannedMillsheet.id),
  )
  // ソート済みの並びを工事名称でまとめ直した、実際の出力順でグループ化する
  const millsheetGroups = groupMillsheetsByConstruction(orderedMillsheets).map((group) => group.millsheets)

  const handlePrint = useReactToPrint({
    pageStyle,
    onBeforePrint: async () => {
      document.title = 'ミルシート一覧表'
    },
    contentRef: componentRef,
  })

  const summaryPageCount = printTargetFlags.summary
    ? millsheetGroups.reduce((acc, group) => {
        const summaryItems = buildSummaryItems(buildPrintRows(group), summaryColumnConfigs, millsheetSortConfig)
        return acc + chunk(summaryItems, SLICE_NUMBER).length
      }, 0)
    : 0
  const totalPageCount = summaryPageCount + pagePlan.pages.length
  const selectedTargetCount = [
    printTargetFlags.summary,
    printTargetFlags.original,
    printTargetFlags.attachments,
  ].filter(Boolean).length
  const totalItemCount = millsheets.reduce((acc, x) => acc + x.millsheetItems.length, 0)

  const summaryColumnCountExceeded = summaryColumnConfigs.length >= SUMMARY_COLUMN_COUNT_WARNING_THRESHOLD

  const toggleColumn = (type: ColumnConfig['type']) => {
    setColumnConfigs((prev) => prev.map((x) => (x.type === type ? { ...x, active: !x.active } : x)))
  }

  const columnSettingsSection = (
    <PrintColumnSettingsSection
      title="集計表の表示項目"
      subtitle={`${summaryColumnConfigs.length}項目`}
      testIdPrefix="millsheet-print-columns"
      open={columnsOpen}
      onToggleOpen={() => {
        setColumnsOpen((value) => !value)
      }}
      columnConfigs={columnConfigs}
      labels={printColumnTypeLabel}
      reorderableTypes={REORDERABLE_COLUMN_TYPES}
      onToggleColumn={toggleColumn}
      onReorder={(types) => {
        setColumnConfigs((prev) => reorderColumnConfigs(prev, REORDERABLE_COLUMN_TYPES, types))
      }}
    />
  )

  return (
    <>
      <PrintDialogSettingsPane>
        <PrintDialogTabNav tab={tab} onChangeTab={onChangeTab} />
        <PrintDialogSettingsBody>
          <fieldset>
            <legend className="text-sm font-semibold text-foreground">印刷する内容</legend>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">必要な帳票だけを選択してください。</p>
            <div className="mt-3 flex flex-col overflow-hidden rounded-lg border border-border">
              <PrintTargetRow
                index={0}
                checked={printTargetFlags.summary}
                onChange={(checked) => {
                  setPrintTargetFlags((x) => ({ ...x, summary: checked }))
                }}
                label="ミルシート集計表"
                description="選択したミルシートをまとめた一覧表"
                details={
                  <PrintDetailsSection
                    open={columnsOpen}
                    onToggleOpen={() => {
                      setColumnsOpen((value) => !value)
                    }}
                    testId="millsheet-summary-details-toggle"
                  >
                    <fieldset>
                      <legend className="text-xs font-semibold text-foreground">帳票タイトル・記載情報</legend>
                      <div className="mt-3 flex flex-col gap-3">
                        <PrintDialogField
                          label="タイトル（集計表）"
                          value={headerLabelTextConfig.summaryTitle ?? ''}
                          placeholder={DEFAULT_SUMMARY_TITLE}
                          onChange={(value) => {
                            setHeaderLabelTextConfig((prev) => ({ ...prev, summaryTitle: value }))
                          }}
                        />
                        <PrintDialogField
                          label="工事監理者"
                          multiline
                          value={headerLabelTextConfig.constructionManager ?? ''}
                          placeholder={`${DEFAULT_CONSTRUCTION_MANAGER}（2行まで入力できます）`}
                          onChange={(value) => {
                            setHeaderLabelTextConfig((prev) => ({ ...prev, constructionManager: value }))
                          }}
                        />
                        <PrintDialogField
                          label="工事施行者"
                          multiline
                          value={headerLabelTextConfig.constructionExecutor ?? ''}
                          placeholder={`${DEFAULT_CONSTRUCTION_EXECUTOR}（2行まで入力できます）`}
                          onChange={(value) => {
                            setHeaderLabelTextConfig((prev) => ({ ...prev, constructionExecutor: value }))
                          }}
                        />
                      </div>
                    </fieldset>
                    {columnSettingsSection}
                    {summaryColumnCountExceeded && <SummaryColumnWarning count={summaryColumnConfigs.length} />}
                  </PrintDetailsSection>
                }
              />
              <PrintTargetRow
                index={1}
                checked={printTargetFlags.original}
                onChange={(checked) => {
                  setPrintTargetFlags((x) => ({ ...x, original: checked }))
                }}
                label="ミルシート"
                description="各製造会社の証明書PDF"
              />
              <PrintTargetRow
                index={2}
                checked={printTargetFlags.attachments}
                onChange={(checked) => {
                  setPrintTargetFlags((x) => ({ ...x, attachments: checked }))
                }}
                label="添付資料"
                description="登録済みの関連資料"
              />
            </div>
          </fieldset>
        </PrintDialogSettingsBody>

        <PrintDialogFooter
          summary={
            totalItemCount === 0
              ? '紐付いている明細がありません'
              : `${selectedTargetCount}種類 / 全${totalPageCount}ページを印刷`
          }
        >
          <Button
            className="gap-2 px-4"
            data-testid="millsheet-print-execute"
            disabled={millsheets.length === 0 || selectedTargetCount === 0}
            onClick={() => {
              handlePrint()
            }}
          >
            <Printer className="size-4" />
            印刷する
          </Button>
        </PrintDialogFooter>
      </PrintDialogSettingsPane>

      <PrintDialogPreviewPane>
        <PrintDialogPreviewToolbar pageCount={totalPageCount} zoom={zoomControls} />
        {millsheets.length === 0 ? (
          <div
            className="flex flex-1 items-center justify-center p-8 text-center text-sm text-muted-foreground"
            data-testid="millsheet-print-empty"
          >
            選択したミルシートがありません。
          </div>
        ) : (
          <div
            ref={viewportRef}
            className="min-h-0 flex-1 overflow-auto p-5 lg:p-7"
            data-testid="millsheet-print-preview"
          >
            <div className="mx-auto bg-white shadow-md" style={{ width: pageSize.width, zoom }}>
              <div ref={componentRef}>
                {millsheetGroups.map((group) => {
                  const constructionName = group[0]?.constructionName ?? ''
                  const rows = buildPrintRows(group)
                  const summaryItems = buildSummaryItems(rows, summaryColumnConfigs, millsheetSortConfig)
                  return (
                    <Fragment key={constructionName}>
                      {printTargetFlags.summary &&
                        chunk(summaryItems, SLICE_NUMBER).map((pageItems, pageIndex) => (
                          <div
                            key={pageIndex}
                            data-testid="print-summary-page"
                            data-construction-name={constructionName}
                          >
                            <MillsheetListSvgV2
                              sumKg=""
                              headerLabelTextConfig={headerLabelTextConfig}
                              columnConfigs={summaryColumnConfigs}
                              indexStart={1 + pageIndex * SLICE_NUMBER}
                              date=""
                              constructionName={constructionName}
                              items={pageItems}
                              asSummary
                            />
                          </div>
                        ))}
                      {pagePlan.pages
                        .filter((page) => group.some((millsheet) => millsheet.id === page.millsheetId))
                        .map((page) => (
                          <PrintPlannedPage key={`${page.s3ObjectId}-${page.sourcePageIndex}`} page={page} />
                        ))}
                    </Fragment>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </PrintDialogPreviewPane>
    </>
  )
}

// 「データ出力」タブ。印刷設定タブとは別コンポーネントにして、タブを切り替えたら丸ごと着脱する
// （発注照合の ExportTab と同じ形。印刷設定タブ側に置いたままプレビューの DOM だけを消すと、
// useResize の ResizeObserver がマウント時にしか張られないためプレビュー倍率が復帰しなくなる）。
// Excel の表示項目設定（並び順と方向）は親（MillsheetPrintDialogContent）が状態を持ち、props で受け取る。
// 印刷設定タブの原本の順と同じソート結果を使うため、ここで別に読み直さない
function ExportTab({
  millsheets,
  images,
  printTargetFlags,
  columnConfigs,
  setColumnConfigs,
}: {
  // ダイアログの入口でソート済みの配列（印刷設定タブの原本の順と同じもの）
  millsheets: MillsheetRow[]
  images: S3ObjectForPrint[]
  printTargetFlags: PrintTargetFlags
  columnConfigs: ColumnConfig[]
  setColumnConfigs: Dispatch<SetStateAction<ColumnConfig[]>>
}) {
  const [columnsOpen, setColumnsOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [includeAllEndorsements, setIncludeAllEndorsements] = useLocalStorageState<boolean>(
    APP_LOCAL_STORAGE_KEYS.millsheetExcelIncludeAllEndorsements,
    false,
    z.boolean(),
  )

  const activeColumnConfigs = fixColumnOrderFor(columnConfigs)
  const pageSources = buildPrintPagePlanSources({ millsheets, images })
  const pagePlan = buildPrintPagePlan({
    millsheets: millsheets.map((millsheet) => ({ id: millsheet.id, constructionName: millsheet.constructionName })),
    sources: pageSources,
    targets: printTargetFlags,
  })
  // 原本の印刷順（工事でまとめ直した順）と Excel・CSV の行順を揃える
  const orderedMillsheets = pagePlan.orderedMillsheets.flatMap((plannedMillsheet) =>
    millsheets.filter((millsheet) => millsheet.id === plannedMillsheet.id),
  )
  // Excel / CSV のページ番号列は、そのミルシートの開始ページ（最初に印刷される物理ページの番号）。
  // 工事名称ごとに Excel を分けても、番号は選択全体での物理ページ番号を維持する。
  // itemCount: 1 で呼ぶのは、明細ごとの枝番（N-1）を付けず全明細行に開始ページを出すため
  const millsheetPageNumberMap = new Map<string, number | string>(
    pagePlan.orderedMillsheets.map((millsheet) => {
      const pageNo = findMillsheetStartPageNo(pagePlan.pages, millsheet.id)
      const pageLabel =
        buildSummaryPageLabels({
          pageNo,
          itemCount: 1,
          hasPrintableOriginal: hasPrintableMillsheetPage(pageSources, millsheet.id),
          originalSelected: printTargetFlags.original,
        })[0] ?? 'PDFなし'
      return [millsheet.id, pageLabel]
    }),
  )

  const handleDownloadExcel = async () => {
    setDownloading(true)
    try {
      for (const group of groupMillsheetsByConstruction(orderedMillsheets)) {
        await downloadMillsheetListExcel({
          items: millsheetsToExcelItems(group.millsheets, millsheetPageNumberMap),
          constructionName: group.constructionName,
          columnConfigs: activeColumnConfigs,
          includeAllEndorsements,
          columnLabelOverrides: HOME_EXCEL_COLUMN_LABEL_OVERRIDES,
          fileNameLabel: group.constructionName,
        })
      }
    } finally {
      setDownloading(false)
    }
  }

  return (
    <PrintExportBody>
      <PrintExportCard
        icon={<FileSpreadsheet className="size-5" />}
        title="使用鋼材一覧表"
        format="Excel（.xlsx）"
        description="一覧を表計算ソフトで編集・集計できます。工事名称ごとに 1 ファイルずつ出力します。"
        disabled={downloading || millsheets.length === 0 || activeColumnConfigs.length === 0}
        testId="millsheet-print-download-excel"
        onDownload={handleDownloadExcel}
        detailsOpen={columnsOpen}
        onToggleDetails={() => {
          setColumnsOpen((value) => !value)
        }}
        details={
          <div className="flex flex-col gap-3">
            <PrintColumnSettingsSection
              title="Excelの表示項目"
              subtitle={`${activeColumnConfigs.filter((x) => x.type !== 'pageNumber').length}項目`}
              testIdPrefix="millsheet-print-excel-columns"
              open
              columnConfigs={columnConfigs}
              labels={printColumnTypeLabel}
              reorderableTypes={REORDERABLE_COLUMN_TYPES}
              onToggleColumn={(type) => {
                setColumnConfigs((prev) => prev.map((x) => (x.type === type ? { ...x, active: !x.active } : x)))
              }}
              onReorder={(types) => {
                setColumnConfigs((prev) => reorderColumnConfigs(prev, REORDERABLE_COLUMN_TYPES, types))
              }}
              onToggleDirection={(type) => {
                // direction 未保存の項目は既定（項目ごとに違う）から反転させる。x.direction だけ見ると
                // 既定が降順の項目で「押しても降順のまま」になる
                setColumnConfigs((prev) =>
                  prev.map((x) =>
                    x.type === type ? { ...x, direction: resolvePrintSortDirection(x) === 'desc' ? 'asc' : 'desc' } : x,
                  ),
                )
              }}
            />
            <PrintColumnCheckboxRow
              label="すべての裏書情報を含める"
              checked={includeAllEndorsements}
              onToggle={() => {
                setIncludeAllEndorsements((prev) => !prev)
              }}
            />
          </div>
        }
      />
      <PrintExportCard
        icon={<FileText className="size-5" />}
        title="ミルシート明細一覧表"
        format="CSV（.csv）"
        description="選択したミルシートの明細を汎用形式で出力します。"
        disabled={millsheets.length === 0}
        testId="millsheet-print-download-csv"
        onDownload={async () => {
          await downloadMillsheetCsv(orderedMillsheets)
        }}
      />
    </PrintExportBody>
  )
}
